import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { DevCheckoutPage } from './components/billing';
import { RecoveryCancelPage } from './components/recovery';
import { sessionQuery } from './lib/session';
import { AccountPage } from './routes/account';
import { ChatPage } from './routes/chat';
import { ConnectorDetailPage, ConnectorsPage } from './routes/connectors';
import { JobsPage } from './routes/jobs';
import { LoginPage } from './routes/login';
import { Shell } from './routes/shell';
import { WorkstationPage } from './routes/workstation';

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: Outlet });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (s: Record<string, unknown>) =>
    typeof s.next === 'string' && s.next.startsWith('/') ? { next: s.next } : {},
  beforeLoad: async ({ context, search }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    if (session) throw redirect({ to: search.next ?? '/chat' });
  },
  component: LoginPage,
});

/** Public: the single-use cancel link from the recovery alert email. */
const recoveryCancelRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/recovery/cancel',
  validateSearch: (s: Record<string, unknown>) =>
    typeof s.token === 'string' ? { token: s.token } : {},
  component: function RecoveryCancelRoute() {
    const { token } = recoveryCancelRoute.useSearch();
    return <RecoveryCancelPage token={token} />;
  },
});

/** Everything under here needs a session; unauthenticated users bounce to /login?next=… */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    if (!session) throw redirect({ to: '/login', search: { next: location.href } });
    return { session };
  },
  component: Shell,
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/workstation' });
  },
});

const workstationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/workstation',
  component: WorkstationPage,
});

const chatRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chat',
  component: ChatPage,
});

const threadRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chat/$threadId',
  component: ChatPage,
});

const jobsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/jobs',
  component: JobsPage,
});

const jobRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/jobs/$jobId',
  component: JobsPage,
});

const connectorsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/connectors',
  component: ConnectorsPage,
});

const connectorRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/connectors/$name',
  component: ConnectorDetailPage,
});

const accountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/account',
  validateSearch: (s: Record<string, unknown>) => ({
    ...(s.enrol === true || s.enrol === 'true' ? { enrol: true } : {}),
    ...(typeof s.purchase === 'string' ? { purchase: s.purchase } : {}),
    ...(s.outcome === 'success' || s.outcome === 'cancel' ? { outcome: s.outcome } : {}),
  }),
  component: AccountPage,
});

/** DEV ONLY: stand-in for Stripe Checkout when the API runs the fake billing gateway. */
const devCheckoutRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dev/checkout',
  validateSearch: (s: Record<string, unknown>) => ({
    ...(typeof s.purchaseId === 'string' ? { purchaseId: s.purchaseId } : {}),
    ...(typeof s.success === 'string' ? { success: s.success } : {}),
    ...(typeof s.cancel === 'string' ? { cancel: s.cancel } : {}),
  }),
  component: function DevCheckoutRoute() {
    const { purchaseId, success, cancel } = devCheckoutRoute.useSearch();
    return <DevCheckoutPage purchaseId={purchaseId} success={success} cancel={cancel} />;
  },
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  recoveryCancelRoute,
  appRoute.addChildren([
    indexRoute,
    workstationRoute,
    chatRoute,
    threadRoute,
    jobsRoute,
    jobRoute,
    connectorsRoute,
    connectorRoute,
    accountRoute,
    devCheckoutRoute,
  ]),
]);

export const createAppRouter = (queryClient: QueryClient) =>
  createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    scrollRestoration: true,
  });
